/**
 * Smoothly scroll the host page to the top of the embedded widget.
 *
 * The widget renders into `<tradein-app>` somewhere down a storefront page, and the *page* is what
 * scrolls (not the widget). After an action that swaps the view — e.g. confirming a match, which
 * closes the dialog and resets the finder — the customer would otherwise be left staring at wherever
 * they'd scrolled to (often the storefront footer). This brings the trade-in flow back into view.
 *
 * We land just above the topbar's resting position, honouring the configurable `--ti-sticky-offset`
 * so we don't scroll behind a host's own sticky header. Deferred a tick so it runs *after* the
 * dialog's focus-restore (which can otherwise scroll the old trigger back into view).
 *
 * General rule (every caller — route change, add, refine): we only ever pull the page *up* to the
 * widget. If the customer is already at or above the target, we skip entirely — scrolling the page
 * *down* to the widget is just noise for someone who hasn't scrolled away from it.
 */
export function scrollWidgetToTop(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  const host = document.querySelector('tradein-app')
  if (!host) return

  window.setTimeout(() => {
    const rect = host.getBoundingClientRect()
    const rawOffset = getComputedStyle(host).getPropertyValue('--ti-sticky-offset').trim()
    const offset = rawOffset ? parseFloat(rawOffset) || 0 : 0
    // rect.top + scrollY = the widget's absolute page position (stable regardless of current scroll).
    const top = Math.max(0, window.scrollY + rect.top - offset)
    // Already at/above the widget top → nothing to bring into view; don't yank the page down.
    if (window.scrollY <= top) return
    window.scrollTo({ top, behavior: 'smooth' })
  }, 60)
}
